// src/App.tsx
import { Route, Routes, NavLink, Link } from "react-router-dom";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Areas from "./pages/Areas";
import Contact from "./pages/Contact";
import Privacy from "./pages/privacy"; // ✅ ADD THIS LINE
import CookieConsent from "./components/CookieConsent";
import { useEffect } from "react";
import { hasAccepted } from "./lib/consent";
import { enableAnalytics } from "./lib/firebase";

// BRAND ASSETS
import smallLogo from "./assets/images/smalllogo.png"; // red/blue mark
import van from "./assets/images/van.jpeg"; 

export default function App() {
  useEffect(() => {
    if (hasAccepted()) enableAnalytics();
  }, []);

  const title = "Beaumonts Plumbing & Heating | Plumbers in Lincoln";
  const description =
    "Professional plumbers in Lincoln. Boilers, bathrooms, repairs and maintenance with clear pricing and neat workmanship.";
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const href =
    typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="min-h-dvh flex flex-col bg-white text-neutral-900">
      {/* HEAD TAGS */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={href} />
      <link rel="icon" type="image/png" href={smallLogo} />
      <link rel="apple-touch-icon" href={smallLogo} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={href} />
      <meta property="og:image" content={`${origin}${van}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${origin}${van}`} />

      {/* HEADER */}
      <header className="border-b bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="mx-auto max-w-7xl px-3 sm:px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 md:gap-3 min-w-0">
            <img
              src={smallLogo}
              alt="Beaumonts Plumbing & Heating logo mark"
              className="h-7 w-7 sm:h-8 sm:w-8 object-contain flex-shrink-0"
              loading="eager"
              decoding="async"
            />
            <span
              className="
                font-semibold tracking-tight text-neutral-900
                text-[14px] sm:text-[15px] md:text-lg
                whitespace-nowrap overflow-hidden text-ellipsis
                max-w-[58vw] sm:max-w-[60vw] md:max-w-none
              "
              title="Beaumonts Plumbing & Heating"
            >
              Beaumonts Plumbing &amp; Heating
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-sm">
            {[
              { to: "/", label: "Home" },
              { to: "/services", label: "Services" },
              { to: "/areas", label: "Areas" },
              { to: "/contact", label: "Contact" },
            ].map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `hover:opacity-80 transition-opacity ${
                    isActive ? "font-medium text-neutral-900" : "text-neutral-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pl-4 border-l border-neutral-200">
              <a href="tel:+441522692592" className="text-neutral-700 hover:text-neutral-900">
                01522 692592
              </a>
              <span className="text-neutral-300">•</span>
              <a href="tel:+447803608738" className="text-neutral-700 hover:text-neutral-900">
                07803 608738
              </a>
            </div>
          </nav>

          <div className="md:hidden flex items-center gap-2">
            <a
              href="tel:+441522692592"
              className="inline-flex items-center rounded-md border border-neutral-300 px-3 py-2 text-xs font-medium hover:bg-neutral-50"
            >
              Call
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center rounded-md bg-[#3bb3ef] text-white px-3 py-2 text-xs font-medium"
            >
              Quote
            </Link>
          </div>
        </div>
      </header>

      {/* ROUTES */}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/areas" element={<Areas />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} /> {/* ✅ ADD THIS */}
        </Routes>
      </main>

      {/* FOOTER */}
      <footer className="border-t">
        <div className="mx-auto max-w-7xl px-4 py-8 text-sm text-neutral-600 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p>© {new Date().getFullYear()} Beaumonts Plumbing &amp; Heating. All rights reserved.</p>
          <Link to="/privacy" className="text-neutral-500 hover:text-neutral-800">
            Privacy Policy
          </Link>
        </div>
      </footer>

      <CookieConsent />
    </div>
  );
}

